import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import {
    Grid, Paper, Typography, useTheme, Button, Divider,
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, ToggleButton, ToggleButtonGroup, Autocomplete,
} from '@mui/material';

import { ReactComponent as RepairIcon } from 'assets/images/repair_icon.svg';
import { ReactComponent as ServiceIcon } from 'assets/images/service_icon.svg';

import { useJobs, TYPES as JOB_TYPES } from 'services/job';
import { cancelService, declineService } from 'services/actions/service';
import { updateJobDetails } from 'services/job/api';
import { useError } from 'contexts/ErrorContext';
import { useCompany } from 'services/company';
import { refreshCompany } from 'services/company/actions';
import { getColors } from 'services/utils/enums';

import Loading from 'components/Loading';
import RepairerCards from './RepairerCards';

import ServiceBoard from './ServiceBoard';

import StarRateIcon from '@mui/icons-material/StarRate';
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined';

const quickOptionsEnums = {
    'CANCELLABLE': ['OPEN'],
    'DECLINABLE': ['OPEN', 'ACCEPTED', 'IN_PROGRESS'],
    'REWORKABLE': ['COMPLETED', 'CLOSED', 'INVOICED'],
    'REASSIGNABLE': ['OPEN', 'CANCELED'],
};

const capitalize = (str) => {
    if (!str || typeof str !== 'string') {
        return '';
    }

    return str.toLowerCase()
        .replace(/\b\w/g, (match) => match.replace(/\w/, (m) => m.toUpperCase()));
};

const JobDetails = ({ activeJob }) => {
    if (!activeJob) {
        return <Loading loading fullScreen={false} width={150} height={150} timeoutMessage={'Could not load job.' } />;
    }

    const jobsContext = useJobs();
    const theme = useTheme();
    const err = useError();
    const companyContext = useCompany();
    const isRepairer = companyContext.state.company?.marketplace_side === 'Repairer';

    useEffect(() => {
        if (!companyContext.state.company?.marketplace_side) {
            refreshCompany(companyContext, err);
        }
    }, []);

    const [showEditDialog, setShowEditDialog] = useState(false);
    const [editForm, setEditForm] = useState({ stockNumber: '', poNumber: '', odometer: '', odometerUnit: 'mi', color: '' });
    const [editLoading, setEditLoading] = useState(false);
    const [colorOptions, setColorOptions] = useState([]);

    const openEditDialog = async () => {
        const odo = activeJob.vehicle?.odometer;
        setEditForm({
            stockNumber: activeJob.vehicle?.stock_number || '',
            poNumber: activeJob.po_number || '',
            odometer: (typeof odo === 'object' ? odo?.value : odo) || '',
            odometerUnit: (typeof odo === 'object' ? odo?.unit : activeJob.vehicle?.odometer_unit) || 'mi',
            color: activeJob.vehicle?.color || '',
        });
        if (!colorOptions.length) {
            const colors = await getColors();
            setColorOptions(colors);
        }
        setShowEditDialog(true);
    };

    const handleCancelButton = async function(option) {
        try {
            await Promise.all(option.payload.map(el => cancelService(el)));
            await jobsContext.DISPATCH({ type: JOB_TYPES.SET_ACTIVE_JOB, payload: activeJob.id }); // Reloads Page
        } catch (error) {
            console.log(error);
            // @ts-ignore
            err.DISPATCH({ type: 'API_ERROR', payload: error });
        }
    };

    const handleDeclineButton = async function(option) {
        try {
            jobsContext.DISPATCH({ type: JOB_TYPES.LOADING, payload: true });
            await Promise.all(option.payload.map(el => declineService(el)));
            await jobsContext.actions.refreshJob(activeJob.id);
        } catch (error) {
            console.log(error);
            err.DISPATCH({ type: 'API_ERROR', payload: error });
        } finally {
            jobsContext.DISPATCH({ type: JOB_TYPES.LOADING, payload: false });
        }
    };

    const handleEditSubmit = async function() {
        setEditLoading(true);
        try {
            await updateJobDetails({
                id: activeJob.id,
                data: {
                    stock_number: editForm.stockNumber, // eslint-disable-line camelcase
                    po_number: editForm.poNumber, // eslint-disable-line camelcase
                    odometer: editForm.odometer,
                    odometer_unit: editForm.odometerUnit, // eslint-disable-line camelcase
                    color: editForm.color,
                },
            });
            setShowEditDialog(false);
            await jobsContext.actions.refreshJob(activeJob.id);
        } catch (error) {
            console.log(error);
            err.DISPATCH({ type: 'API_ERROR', payload: error });
        } finally {
            setEditLoading(false);
        }
    };

    const getYYMC = function() {
        let yymc = [];

        if (activeJob.vehicle) {
            if (activeJob.vehicle.year || activeJob.vehicle.make || activeJob.vehicle.model) {
                yymc.push(
                    `${activeJob.vehicle.year || ''} ` +
                    `${capitalize(activeJob.vehicle.make) || ''} ` +
                    `${capitalize(activeJob.vehicle.model) || ''}`,
                );
            }
            if (activeJob.vehicle.color) {
                yymc.push(capitalize(activeJob.vehicle.color));
            }
        }
        return yymc.join(', ');
    };

    const dateCreated = `Created on: ${new Date(activeJob.created_at).toDateString()}`;

    const getStockRO = function() {
        if (activeJob.vehicle) {
            return `Stock/RO: ${activeJob.vehicle.stock_number || ''}`;
        }
    };

    const getVIN = function() {
        if (activeJob.vehicle) {
            return `VIN: ${activeJob.vehicle.vin || ''}`;
        }
    };

    const getPO = function() {
        return `PO#: ${activeJob.po_number || ''}`;
    };

    const getLocation = function() {
        return activeJob.location.nickname || activeJob.location.formatted_address || '';
    };

    const hasRatings = function() {
        if (!activeJob.reviews || activeJob.reviews.length === 0) {
            return false;
        }

        // Get all unique repairer companies for this job
        const uniqueRepairerCompanies = [...new Set(activeJob.services.map(el => el.repair_company.id))];

        // Check if all repairer companies have ratings
        return uniqueRepairerCompanies.every(repairerId =>
            activeJob.reviews.some(review => review.reviewed_company.id === repairerId));
    };

    return (
        <Paper elevation={0} sx={{ pb: 4, width: '100%' }}>
            { activeJob.vehicle && activeJob.services && (
                <Grid container justifyContent={'center'} display={'flex'}>
                    <Grid
                        item
                        sx={{ height: '50px', width: '100%', px: 2 }}
                        alignItems={'center'}
                        display={'flex'}
                        justifyContent={'space-between'}
                    >
                        <Typography sx={{ fontSize: 12, fontWeight: 300 }} color={theme.palette.grey[500]}> {dateCreated} </Typography>
                        <Grid item display={'flex'} alignItems={'center'}>
                            <Button
                                sx={{
                                    textTransform: 'uppercase',
                                    fontSize: 10,
                                    borderRadius: theme.spacing(1),
                                    padding: '2px 8px',
                                    marginRight: theme.spacing(1),
                                    background: theme.palette.blue.main,
                                    color: theme.palette.white,
                                    '&: hover': {
                                        background: theme.palette.blue.main,
                                        color: theme.palette.white,
                                    },
                                }}
                                onClick={openEditDialog}
                            > Edit Details </Button>
                            {!isRepairer && activeJob.services.filter(
                                el => quickOptionsEnums['CANCELLABLE'].includes(el.service_status.toUpperCase()),
                            ).length > 0 && (
                                <Button
                                    sx={{
                                        textTransform: 'uppercase',
                                        fontSize: 10,
                                        borderRadius: theme.spacing(1),
                                        padding: '2px 8px',
                                        marginRight: theme.spacing(1.5),
                                        marginLeft: theme.spacing(1.5),
                                        background: theme.palette.blue.main,
                                        color: theme.palette.white,
                                        '&: hover': {
                                            background: theme.palette.blue.main,
                                            color: theme.palette.white,
                                        },
                                    }}
                                    onClick={() => {
                                        handleCancelButton(
                                            {
                                                payload: activeJob.services.filter(
                                                    el => quickOptionsEnums['CANCELLABLE']
                                                        .includes(el.service_status.toUpperCase()),
                                                )
                                                    .map(el => ({ serviceID: el.id })),
                                            },
                                        );
                                    }}
                                > Cancel Job </Button>
                            )}
                            {isRepairer && activeJob.services.filter(
                                el => quickOptionsEnums['DECLINABLE'].includes(el.service_status.toUpperCase().replace(/ /g, '_')),
                            ).length > 0 && (
                                <Button
                                    sx={{
                                        textTransform: 'uppercase',
                                        fontSize: 10,
                                        borderRadius: theme.spacing(1),
                                        padding: '2px 8px',
                                        marginRight: theme.spacing(1.5),
                                        background: theme.palette.error.main,
                                        color: theme.palette.white,
                                        '&: hover': {
                                            background: theme.palette.error.dark,
                                            color: theme.palette.white,
                                        },
                                    }}
                                    onClick={() => {
                                        handleDeclineButton(
                                            {
                                                payload: activeJob.services.filter(
                                                    el => quickOptionsEnums['DECLINABLE']
                                                        .includes(el.service_status.toUpperCase().replace(/ /g, '_')),
                                                )
                                                    .map(el => ({ serviceID: el.id })),
                                            },
                                        );
                                    }}
                                > Decline Job </Button>
                            )}
                        </Grid>
                    </Grid>
                    <Divider style={{ width: '95%', marginTop: 1, marginBottom: 1 }} />
                    <Typography
                        mt={1}
                        display={'flex'}
                        sx={{ width: '100%', px: 2, fontSize: 20, fontWeight: 500 }}
                    >
                        { getYYMC() }
                    </Typography>
                    <Grid item display={'flex'} sx={{ width: '100%', px: 2 }} justifyContent={'space-between'} my={1}>
                        <Grid item>
                            <Typography
                                sx={{ fontSize: 12, fontWeight: 300 }}
                                color={theme.palette.grey[500]}
                            >
                                { getStockRO() }
                            </Typography>
                            <Typography
                                sx={{ fontSize: 12, fontWeight: 300, textTransform: 'uppercase' }}
                                color={theme.palette.grey[500]}
                            >
                                { getVIN() }
                            </Typography>
                            <Typography
                                sx={{ fontSize: 12, fontWeight: 300 }}
                                color={theme.palette.grey[500]}
                            >
                                { getLocation() }
                            </Typography>
                        </Grid>
                        <Typography sx={{ fontSize: 12, fontWeight: 300 }} color={theme.palette.grey[500]}> { getPO() } </Typography>
                        {/* <ChatButton> <ChatBubbleOutline /> </ChatButton> */}
                    </Grid>
                    <Divider style={{ width: '95%', marginTop: 1, marginBottom: 1 }} />
                    <Grid item display={'flex'} sx={{ width: '100%', px: 2 }} justifyContent={'space-between'} alignItems={'center'} my={1}>
                        <Grid item display={'flex'} alignItems={'center'}>
                            <ServiceIcon />
                            <Typography
                                mx={1}
                                sx={{ fontSize: 12, fontWeight: 500 }}
                                color={theme.palette.grey[900]}
                            >
                                { activeJob.services.length }
                            </Typography>
                            <RepairIcon />
                            <Typography
                                mx={1}
                                sx={{ fontSize: 12, fontWeight: 500 }}
                                color={theme.palette.grey[900]}
                            >
                                { [...new Set(activeJob.services.map(el => el.repair_company.name))].length }
                            </Typography>
                            {hasRatings() ? (
                                <StarRateIcon
                                    sx={{
                                        ml: 0.5,
                                        color: theme.palette.grey[400],
                                        fontSize: '18px',
                                    }}
                                />
                            ) : (
                                <StarBorderOutlinedIcon
                                    sx={{
                                        ml: 0.5,
                                        color: theme.palette.grey[400],
                                        fontSize: '18px',
                                    }}
                                />
                            )}
                        </Grid>
                        <Typography sx={{ fontSize: 14, fontWeight: 500 }} color={theme.palette.grey[900]}>
                            {
                                new Intl.NumberFormat(
                                    'en-US',
                                    { style: 'currency', currency: 'USD' },
                                ).format(activeJob.services.reduce((sum, part) => sum += part.price, 0))
                            }
                        </Typography>
                        {/* <ChatButton> <ChatBubbleOutline /> </ChatButton> */}
                    </Grid>
                    <Divider style={{ width: '95%', marginTop: 2 }} />
                    <ServiceBoard services={activeJob.services} />
                    <RepairerCards activeJob={activeJob} quickOptionsEnums={quickOptionsEnums} />
                </Grid>
            )}

            <Dialog open={showEditDialog} onClose={() => setShowEditDialog(false)} maxWidth='sm' fullWidth>
                <DialogTitle>Edit Job Details</DialogTitle>
                <DialogContent>
                    <TextField
                        label='Stock/RO Number'
                        fullWidth
                        margin='normal'
                        value={editForm.stockNumber || ''}
                        onChange={(e) => setEditForm({ ...editForm, stockNumber: e.target.value })}
                    />
                    <TextField
                        label='PO Number'
                        fullWidth
                        margin='normal'
                        value={editForm.poNumber || ''}
                        onChange={(e) => setEditForm({ ...editForm, poNumber: e.target.value })}
                    />
                    <Autocomplete
                        options={colorOptions}
                        value={editForm.color || null}
                        onChange={(e, newValue) => setEditForm({ ...editForm, color: newValue || '' })}
                        renderInput={(params) => <TextField {...params} label='Color' fullWidth margin='normal' />}
                        freeSolo
                    />
                    <TextField
                        label='Odometer'
                        fullWidth
                        margin='normal'
                        type='number'
                        value={editForm.odometer || ''}
                        onChange={(e) => setEditForm({ ...editForm, odometer: e.target.value })}
                    />
                    <Typography sx={{ fontSize: 12, mt: 1, mb: 0.5 }} color={theme.palette.grey[500]}>
                        Odometer Unit
                    </Typography>
                    <ToggleButtonGroup
                        value={editForm.odometerUnit || 'mi'}
                        exclusive
                        onChange={(e, val) => val && setEditForm({ ...editForm, odometerUnit: val })}
                        size='small'
                    >
                        <ToggleButton value='mi'>Miles</ToggleButton>
                        <ToggleButton value='km'>Kilometers</ToggleButton>
                    </ToggleButtonGroup>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowEditDialog(false)}>Cancel</Button>
                    <Button onClick={handleEditSubmit} variant='contained' disabled={editLoading}>
                        {editLoading ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

        </Paper>
    );
};

JobDetails.propTypes = {
    activeJob: PropTypes.object,
};

export default JobDetails;
